import React from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Avatar from '@material-ui/core/Avatar';
import { NavLink, Route } from 'react-router-dom';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { getInitials, toUserTimezone } from 'utils';
import { LISTEN_USER_ROOMS } from 'graphql/chat';
import Spin from 'components/common/Spin';
import { Subscription, useSubscription } from 'react-apollo';
import groupBy from 'lodash/groupBy';
import StudentChatRooms from './StudentChatRooms';
import moment from 'moment';
import { LISTEN_USER_UNREAD_COUNT } from 'graphql/chat';

const useStyles = makeStyles((theme) => ({
  listGrid: {
    [theme.breakpoints.down('xs')]: {
      display: ({ selected }) => (selected ? 'none' : 'block'),
    },
  },
  card: {
    height: '100%',
    position: 'relative',
    zIndex: 1,
  },
  listRoot: {
    '& .MuiListItem-root': {
      'borderLeft': '2px solid transparent',
      '&:hover': {
        backgroundColor: theme.palette.primary['200'],
      },
    },
    '& .Mui-selected': {
      'borderColor': theme.palette.primary.main,
      'backgroundColor': theme.palette.primary['200'],
      '& .MuiListItemIcon-root': {
        color: theme.palette.primary.main,
        opacity: 1,
      },
      '& .MuiTypography-root': {
        fontWeight: 700,
      },
    },
    '& .MuiListItemIcon-root': {
      minWidth: 36,
      opacity: 0.5,
    },
  },
  roomIcon: {
    marginRight: theme.spacing(2),
  },
  roomHeader: {
    padding: '0 24px',
    boxShadow: '0px 5px 47px rgba(44, 9, 12, 0.05)',
    zIndex: 1,
  },
  secondaryAction: {
    paddingTop: 8,
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'block',
    },
  },
  badge: {
    backgroundColor: theme.palette.secondary.main,
    color: 'white',
    padding: '1px 5px',
    borderRadius: 20,
    fontSize: 12,
    fontWeight: 600,
  },
}));

const Inbox = ({ match }) => {
  const studentId = match.params.studentId;
  const classes = useStyles({ selected: !!studentId });
  const user = useSelector((s) => s.user.user);
  const { data } = useSubscription(LISTEN_USER_UNREAD_COUNT, {
    variables: { userId: user.id },
  });

  const renderSecondary = () => {
    if (data) {
      const totalCount = data.v_chat_unread_counts;
      const inboxChatCount = totalCount
        .filter((i) => i.room.room_name !== 'groups')
        .reduce((memo, r) => {
          return memo + r.count;
        }, 0);
      if (inboxChatCount) {
        return (
          <ListItemSecondaryAction>
            <span className={classes.badge}>{inboxChatCount}</span>
          </ListItemSecondaryAction>
        );
      }
      return null;
    }
    return null;
  };

  return (
    <Box>
      <Helmet title="Chat" />
      <Grid container>
        <Grid item xs={12} sm={5} md={5} lg={4} className={classes.listGrid}>
          <Card className={classes.card}>
            <CardHeader
              title="Students"
              titleTypographyProps={{
                variant: 'h5',
              }}
            />
            <Subscription
              subscription={LISTEN_USER_ROOMS}
              variables={{ userId: user.id, sortParams: { unread: 'desc' } }}
            >
              {({ data, loading, error }) => {
                if (loading) return <Spin linear spinning />;
                if (error) return error.message;

                const { rooms } = data;
                const grouped = groupBy(rooms, 'context_id');
                return (
                  <List className={classes.listRoot}>
                    {Object.keys(grouped).map((id) => {
                      const student = grouped[id][0].room.student;
                      const isUnread =
                        grouped[id][0].unread || grouped[id][1].unread;
                      const lastMsgDates =
                        grouped[id][0].last_message_at &&
                        grouped[id][1].last_message_at
                          ? [
                              moment(grouped[id][0].last_message_at),
                              moment(grouped[id][1].last_message_at),
                            ]
                          : grouped[id][0].last_message_at
                          ? [moment(grouped[id][0].last_message_at)]
                          : grouped[id][1].last_message_at
                          ? [moment(grouped[id][1].last_message_at)]
                          : null;
                      const lastMsgAt = lastMsgDates
                        ? moment.max(lastMsgDates).format('MM-DD-YY h:mm A')
                        : null;

                        return (
                        <ListItem
                          key={id}
                          button
                          component={NavLink}
                          to={`/inbox/${id}`}
                          selected={id === studentId}
                          activeClassName="Mui-selected"
                        >
                          <ListItemIcon>
                            <Avatar
                              className={classes.roomIcon}
                              src={student.user_pic}
                            >
                              {getInitials(student.user_first_name)}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primaryTypographyProps={{
                              variant: 'body2',
                            }}
                            secondaryTypographyProps={{
                              variant: 'caption'
                            }}
                            primary={student.user_first_name}
                            secondary={lastMsgAt}
                          />
                          {isUnread && renderSecondary()}
                          <ListItemSecondaryAction
                            className={classes.secondaryAction}
                          >
                            <ArrowForwardIcon
                              color="primary"
                              fontSize="small"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                );
              }}
            </Subscription>
          </Card>
        </Grid>
        <Grid item sm={12} sm={7} md={7} lg={8}>
          <Card>
            <Box marginBottom={{ xs: 14, sm: 0 }}>
              <Route path="/inbox/:studentId" component={StudentChatRooms} />
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Inbox;
